import { useLoginProvider } from '@account-service/core'
import { useCallback, useEffect, useState } from 'react'

import type { ErrorResponse } from '~/apis'
import { connectSocialAPI, disconnectSocialAPI } from '~/apis'
import { EventName, sendEvent } from '~/apis/tracking'
import { ERRORS } from '~/constants'
import { useSentry } from '~/core/sentry'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useWrappedToast } from '~/hooks/useWrappedToast'

import type { TConnectProps } from '../Connect'

const useHooks = (props: TConnectProps) => {
  const { facebook } = useLoginProvider()
  const { getProfile } = useAuthContext()
  const { showError, showSuccessMessage } = useWrappedToast()
  const { captureException } = useSentry()

  const [loading, setLoading] = useState(false)

  const handleConnect = useCallback(async () => {
    setLoading(true)
    try {
      const [token, facebookError] = await facebook.signIn()

      if (!token || facebookError) {
        showError(ERRORS.ERR_LOGIN_SDK)
        return
      }

      if (!props.connected) {
        await connectSocialAPI({
          service: 'facebook',
          token,
        })
      } else {
        await disconnectSocialAPI({
          service: 'facebook',
          token,
        })
      }

      await getProfile()
      showSuccessMessage(
        `${!props.connected ? 'Link' : 'Unlink'} facebook successfully`,
      )

      const eventTracking = props.connected
        ? EventName.DISCONNECT_FACEBOOK
        : EventName.CONNECT_FACEBOOK

      sendEvent({
        type: 'track',
        data: {
          event: eventTracking,
          action: eventTracking,
          ref: eventTracking,
        },
      })
    } catch (error) {
      facebook.signOut()
      const { error_status } = error as ErrorResponse

      if (
        [
          'ERR_FACEBOOK_OAUTH_TOKEN_INVALID',
          'ERR_LINK_WITH_EXIST_PROFILE',
          'ERR_ACCOUNT_CONNECTED_WITH_OTHER_EMAIL',
          'ERR_OAUTH_TOKEN_INVALID',
        ].includes(error_status)
      ) {
        showError(ERRORS[error_status])
        return
      }

      captureException(error)
    } finally {
      setLoading(false)
    }
  }, [facebook, props.connected])

  useEffect(() => {
    facebook.loadSDK()

    return () => facebook.removeSDK()
  }, [facebook])

  return { loading, handleConnect }
}

export default useHooks
